import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Building2, User, Mail, Phone, Briefcase, Upload, 
  FileText, CheckCircle2, Loader2, AlertCircle, ArrowRight,
  Sparkles, Eye, XCircle, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

// Common trade types
const TRADE_OPTIONS = [
  'General Contractor',
  'Plumbing',
  'Electrical',
  'HVAC',
  'Roofing',
  'Flooring',
  'Drywall',
  'Painting',
  'Concrete',
  'Framing',
  'Insulation',
  'Windows & Doors',
  'Siding',
  'Landscaping',
  'Excavation',
  'Foundation',
  'Cabinetry',
  'Countertops',
  'Tile',
  'Masonry',
  'Gutters',
  'Fencing',
  'Demolition',
  'Cleaning',
  'Other'
];

export default function ContractorSignup() {
  const { signupCode } = useParams();
  const navigate = useNavigate();
  
  // Owner/Company info (loaded from signup code)
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [isLoadingOwner, setIsLoadingOwner] = useState(true);
  const [ownerError, setOwnerError] = useState(null);
  
  // Form state
  const [step, setStep] = useState(1); // 1 = Info, 2 = W-9 Upload, 3 = Success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    trade: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });
  
  // W-9 upload state
  const [w9File, setW9File] = useState(null);
  const [isUploadingW9, setIsUploadingW9] = useState(false);
  const [contractorToken, setContractorToken] = useState(null);
  
  // AI W-9 scanning state
  const [isScanning, setIsScanning] = useState(false);
  const [w9ExtractedData, setW9ExtractedData] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationMismatches, setVerificationMismatches] = useState([]);
  
  // Load owner info from signup code
  useEffect(() => {
    const loadOwnerInfo = async () => {
      try {
        setIsLoadingOwner(true);
        const response = await apiService.getContractorSignupInfo(signupCode);
        setOwnerInfo(response.data);
      } catch (err) {
        console.error('Failed to load signup info:', err);
        setOwnerError('Invalid or expired signup link');
      } finally {
        setIsLoadingOwner(false);
      }
    };
    
    if (signupCode) {
      loadOwnerInfo();
    }
  }, [signupCode]);
  
  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmitInfo = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!form.company_name || !form.contact_name || !form.email || !form.trade) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const response = await apiService.registerContractor(signupCode, {
        company: form.company_name,
        name: form.contact_name,
        email: form.email,
        phone: form.phone,
        trade: form.trade,
        address: form.address,
        city: form.city,
        state: form.state,
        zip: form.zip
      });
      
      setContractorToken(response.data.portal_token);
      toast.success('Registration successful! Now upload your W-9 to continue.');
      setStep(2);
      
    } catch (err) {
      console.error('Registration failed:', err);
      const errorMessage = err.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleW9Upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];
    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.heic')) {
      toast.error('Please upload a PDF or image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }
    
    setW9File(file);
    setW9ExtractedData(null);
    setShowVerification(false);
    setVerificationMismatches([]);
    
    // Automatically scan the W-9
    await scanW9(file);
  };
  
  const scanW9 = async (file) => {
    try {
      setIsScanning(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiService.extractW9Data(formData);
      
      if (response.data.success && response.data.extracted_data) {
        const extracted = response.data.extracted_data;
        setW9ExtractedData(extracted);
        
        // Compare extracted data with form data
        const mismatches = [];
        
        // Check company name
        if (extracted.name || extracted.business_name) {
          const w9Name = (extracted.business_name || extracted.name || '').toLowerCase().trim();
          const formName = form.company_name.toLowerCase().trim();
          if (w9Name && formName && !w9Name.includes(formName) && !formName.includes(w9Name)) {
            mismatches.push({
              field: 'Company Name',
              entered: form.company_name,
              w9Value: extracted.business_name || extracted.name
            });
          }
        }
        
        // Check address
        if (extracted.address) {
          const w9Address = extracted.address.toLowerCase().trim();
          const formAddress = form.address.toLowerCase().trim();
          if (formAddress && w9Address && !w9Address.includes(formAddress) && !formAddress.includes(w9Address)) {
            mismatches.push({
              field: 'Address',
              entered: form.address,
              w9Value: extracted.address
            });
          }
        }
        
        // Check city
        if (extracted.city) {
          const w9City = extracted.city.toLowerCase().trim();
          const formCity = form.city.toLowerCase().trim();
          if (formCity && w9City && w9City !== formCity) {
            mismatches.push({
              field: 'City',
              entered: form.city,
              w9Value: extracted.city
            });
          }
        }
        
        // Check state
        if (extracted.state) {
          const w9State = extracted.state.toUpperCase().trim();
          const formState = form.state.toUpperCase().trim();
          if (formState && w9State && w9State !== formState) {
            mismatches.push({
              field: 'State',
              entered: form.state,
              w9Value: extracted.state
            });
          }
        }
        
        // Check ZIP
        if (extracted.zip) {
          const w9Zip = extracted.zip.replace(/\D/g, '').substring(0, 5);
          const formZip = form.zip.replace(/\D/g, '').substring(0, 5);
          if (formZip && w9Zip && w9Zip !== formZip) {
            mismatches.push({
              field: 'ZIP Code',
              entered: form.zip,
              w9Value: extracted.zip
            });
          }
        }
        
        setVerificationMismatches(mismatches);
        setShowVerification(true);
        
        if (mismatches.length > 0) {
          toast.warning(`Found ${mismatches.length} difference(s) between your info and W-9. Please review.`);
        } else if (extracted.confidence === 'HIGH') {
          toast.success('W-9 verified! Information matches your registration.');
        } else {
          toast.info('W-9 scanned. Please verify the information is correct.');
        }
      } else {
        toast.info('W-9 uploaded. AI could not extract data - please proceed manually.');
      }
    } catch (err) {
      console.error('W-9 scan failed:', err);
      toast.info('W-9 uploaded. AI scanning unavailable - please proceed.');
    } finally {
      setIsScanning(false);
    }
  };
  
  const handleSubmitW9 = async () => {
    if (!w9File || !contractorToken) {
      toast.error('Please select a W-9 file to upload');
      return;
    }
    
    try {
      setIsUploadingW9(true);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        
        try {
          await apiService.uploadContractorW9Portal(contractorToken, base64, w9File.name);
          toast.success('W-9 uploaded successfully!');
          setStep(3);
        } catch (err) {
          console.error('W-9 upload failed:', err);
          toast.error('Failed to upload W-9. Please try again.');
        } finally {
          setIsUploadingW9(false);
        }
      };
      reader.readAsDataURL(w9File);
      
    } catch (err) {
      console.error('W-9 upload error:', err);
      toast.error('Failed to upload W-9');
      setIsUploadingW9(false);
    }
  };
  
  const handleGoToPortal = () => {
    if (contractorToken) {
      navigate(`/contractor-portal?token=${contractorToken}`);
    }
  };
  
  // Loading state
  if (isLoadingOwner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading registration form...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Error state
  if (ownerError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Link</h2>
            <p className="text-slate-600">{ownerError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Join as a Contractor</h1>
          <p className="text-blue-200">
            Partner with <span className="font-semibold text-white">{ownerInfo?.company_name || ownerInfo?.name}</span>
          </p>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-slate-700 text-slate-400'
              }`}>
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 rounded ${step > s ? 'bg-blue-500' : 'bg-slate-700'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-400 mb-8 px-2">
          <span>Info</span>
          <span>W-9</span>
          <span>Done</span>
        </div>
        
        {/* Step 1: Company Info Form */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                Company Information
              </CardTitle>
              <CardDescription>
                Tell us about your contracting business
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitInfo} className="space-y-4">
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="company_name"
                      placeholder="Your Company Name"
                      className="pl-10"
                      value={form.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="contact_name"
                      placeholder="John Smith"
                      className="pl-10"
                      value={form.contact_name}
                      onChange={(e) => handleInputChange('contact_name', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="trade">Trade/Specialty *</Label>
                  <Select 
                    value={form.trade} 
                    onValueChange={(value) => handleInputChange('trade', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your trade" />
                    </SelectTrigger>
                    <SelectContent>
                      {TRADE_OPTIONS.map(trade => (
                        <SelectItem key={trade} value={trade.toLowerCase()}>
                          {trade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@company.com"
                        className="pl-10"
                        value={form.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        className="pl-10"
                        value={form.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <p className="text-sm text-slate-500 mb-3">Business Address (optional)</p>
                  
                  <div className="space-y-3">
                    <Input
                      id="address"
                      placeholder="Street Address"
                      value={form.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                    />
                    
                    <Input
                      id="city"
                      placeholder="Cedar Rapids"
                      value={form.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          placeholder="IA"
                          maxLength={2}
                          value={form.state}
                          onChange={(e) => handleInputChange('state', e.target.value.toUpperCase())}
                        />
                      </div>
                      <div>
                        <Label htmlFor="zip">ZIP</Label>
                        <Input
                          id="zip"
                          placeholder="52402"
                          maxLength={10}
                          value={form.zip}
                          onChange={(e) => handleInputChange('zip', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registering...</>
                  ) : (
                    <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
        
        {/* Step 2: W-9 Upload */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Upload W-9
              </CardTitle>
              <CardDescription>
                A W-9 is required to become an approved contractor
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  AI will verify your W-9 matches your registration info for accuracy.
                </AlertDescription>
              </Alert>
              
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  w9File ? 'border-green-300 bg-green-50' : 'border-slate-300 hover:border-blue-400'
                }`}
              >
                {isScanning ? (
                  <div className="space-y-2">
                    <Sparkles className="w-12 h-12 text-blue-500 mx-auto animate-pulse" />
                    <p className="font-medium text-slate-900">Scanning W-9 with AI...</p>
                    <p className="text-sm text-slate-500">Verifying information</p>
                  </div>
                ) : w9File ? (
                  <div className="space-y-2">
                    <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                    <p className="font-medium text-slate-900">{w9File.name}</p>
                    <p className="text-sm text-slate-500">
                      {(w9File.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setW9File(null);
                        setW9ExtractedData(null);
                        setShowVerification(false);
                        setVerificationMismatches([]);
                      }}
                    >
                      Choose Different File
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="font-medium text-slate-900 mb-1">
                      Click to upload your W-9
                    </p>
                    <p className="text-sm text-slate-500">
                      PDF, JPG, PNG, or HEIC (max 10MB)
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.heic"
                      onChange={handleW9Upload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              
              {/* AI Verification Results */}
              {showVerification && w9ExtractedData && (
                <div className="space-y-3">
                  {/* Confidence Badge */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">AI Verification</span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      w9ExtractedData.confidence === 'HIGH' 
                        ? 'bg-green-100 text-green-700'
                        : w9ExtractedData.confidence === 'MEDIUM'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {w9ExtractedData.confidence || 'MEDIUM'} Confidence
                    </span>
                  </div>
                  
                  {/* Extracted Data Summary */}
                  <div className="p-3 bg-slate-50 rounded-lg text-sm">
                    <p className="font-medium text-slate-700 mb-2">W-9 Information Detected:</p>
                    <div className="space-y-1 text-slate-600">
                      {w9ExtractedData.name && (
                        <p>Name: <span className="font-medium">{w9ExtractedData.name}</span></p>
                      )}
                      {w9ExtractedData.business_name && (
                        <p>Business: <span className="font-medium">{w9ExtractedData.business_name}</span></p>
                      )}
                      {w9ExtractedData.address && (
                        <p>Address: <span className="font-medium">{w9ExtractedData.address}</span></p>
                      )}
                      {(w9ExtractedData.city || w9ExtractedData.state || w9ExtractedData.zip) && (
                        <p>Location: <span className="font-medium">
                          {[w9ExtractedData.city, w9ExtractedData.state, w9ExtractedData.zip].filter(Boolean).join(', ')}
                        </span></p>
                      )}
                      {w9ExtractedData.tax_classification && (
                        <p>Tax Type: <span className="font-medium">{w9ExtractedData.tax_classification}</span></p>
                      )}
                      {w9ExtractedData.ein && (
                        <p>EIN: <span className="font-medium">{w9ExtractedData.ein}</span></p>
                      )}
                      {w9ExtractedData.signature_present !== undefined && (
                        <p>Signature: <span className={`font-medium ${w9ExtractedData.signature_present ? 'text-green-600' : 'text-red-600'}`}>
                          {w9ExtractedData.signature_present ? 'Present' : 'Missing'}
                        </span></p>
                      )}
                    </div>
                  </div>
                  
                  {/* Mismatches Warning */}
                  {verificationMismatches.length > 0 && (
                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        <p className="font-medium mb-2">Information differences detected:</p>
                        <ul className="space-y-1 text-sm">
                          {verificationMismatches.map((m, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              <span>
                                <strong>{m.field}:</strong> You entered "{m.entered}" but W-9 shows "{m.w9Value}"
                              </span>
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 text-xs">Please ensure your registration info matches your W-9 exactly.</p>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* All Good */}
                  {verificationMismatches.length === 0 && w9ExtractedData.confidence === 'HIGH' && (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        W-9 information matches your registration. You're good to go!
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Rescan button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => scanW9(w9File)}
                    disabled={isScanning}
                    className="w-full"
                  >
                    {isScanning ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Rescan W-9
                  </Button>
                </div>
              )}
              
              <Button 
                className="w-full" 
                disabled={!w9File || isUploadingW9 || isScanning}
                onClick={handleSubmitW9}
              >
                {isUploadingW9 ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <>Complete Registration <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Step 3: Success */}
        {step === 3 && (
          <Card>
            <CardContent className="pt-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome Aboard!</h2>
              <p className="text-slate-600 mb-6">
                You're now registered as a contractor with {ownerInfo?.company_name || ownerInfo?.name}. 
                Access your portal to view bid opportunities and manage your documents.
              </p>
              
              <Button className="w-full" onClick={handleGoToPortal}>
                Go to Contractor Portal <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              
              <p className="text-xs text-slate-500 mt-4">
                Save this link to access your portal anytime
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-slate-400 text-sm">
            Powered by <span className="font-semibold text-blue-400">DrawReady</span>
          </p>
        </div>
      </div>
    </div>
  );
}
